import { vimeoPresets } from '@components/vimeo';

export type Page = {
  id: number;
  slug: string;
  title: string;
  sections: Section[];
  structuredData: StructuredDataSection[];
  url: string;
  locale: string;
  seo: SeoData | null;
  publishedAt: string;
};

// Structured-data-only zone. Unlike `sections` these render nothing visible;
// they exist solely to emit schema.org JSON-LD. `componentName` mirrors
// `Section.sectionName` as the discriminator.
export type StructuredDataSection = ProductSection & {
  componentName: string;
};

export type ProductSection = {
  structuredDataId: string;
  name: string;
  // null (not undefined) so the value survives Next.js props serialization.
  description: string | null;
  imageUrl: string | null;
};

export type Section = (
  | RichText
  | ImageText
  | VideoText
  | Image
  | Video
  | Cards
  | HeroStatic
  | HeroVideo
  | Quote
  | TitleParagraphLinks
  | Team
  | PageTitle
  | Contact
  | Events
  | Vimeo
  | HTML
  | VideoCarousel
  | WhistleblowerForm
) & {
  sectionName: string;
};

export type RichText = {
  title?: string;
  html: string;
};

export type PageTitle = {
  title: string;
};

export type Quote = {
  title: string;
  author: string;
  authorDescription: string;
};

export type TitleParagraphLinks = {
  title: boolean;
  paragraph: string;
  links: Link[];
};

export type Link = {
  text: string;
  url: string;
};

type TeaserText = {
  title: string;
  subTitle: string;
  link: Link | null;
  layout: 'ImageLeft' | 'ImageRight' | null;
  paragraph: string | null;
};

export type ImageText = TeaserText & {
  image: ImageData;
  vimeoUrl?: never;
  videoPreset?: never;
  videoStartAtSecond?: never;
};

export type VideoText = TeaserText & {
  image?: never;
  vimeoUrl: string;
  videoPreset: keyof typeof vimeoPresets | null;
  videoStartAtSecond: number | null;
  thumbnail: ImageData | null;
};

export type Image = {
  image: ImageData;
};

export type Video = {
  video: VideoData;
};

export type VideoData = {
  mime: string;
  rawData: any;
  thumbnail: string;
  title: string;
  url: string;
};

export type ImageFormat = {
  width: number;
  height: number;
  url: string;
};

export type ImageData = {
  id: number;
  alternativeText: string;
  thumbnail: ImageFormat;
  small: ImageFormat | null;
  medium: ImageFormat | null;
  large: ImageFormat | null;
  xlarge: ImageFormat | null;
  xxlarge: ImageFormat | null;
  hash: string;
  ext: string;
  mime: string;
  name: string;
} & ImageFormat;

export type Cards = {
  elements: Card[];
};

export type Card = {
  overline: string;
  title: string;
  html: string;
  link: Link | null;
};

export type FocalPoint =
  | 'bottom'
  | 'center'
  | 'left'
  | 'leftBottom'
  | 'leftTop'
  | 'right'
  | 'rightBottom'
  | 'rightTop'
  | 'top'
  | 'custom';

type Hero = {
  title: string;
  overline?: string;
};

export type HeroStatic = Hero & {
  vimeoUrl?: never;
  cover: ImageData;
  backgroundFocalpoint: FocalPoint;
  customFocalpoint: string;
  videoStartAtSecond?: never;
};

export type HeroVideo = Hero & {
  vimeoUrl: string;
  cover?: never;
  backgroundFocalpoint?: never;
  customFocalpoint: never;
  videoStartAtSecond?: number | null;
};

export type Menu = {
  items: MenuItem[];
};

export type MenuItem = {
  title: string;
  href: string;
  target: string;
  children?: MenuItem[];
};

export enum ContainerSize {
  wide = 'wide',
  full = 'full',
}

export type Settings = {
  footer: SettingsFooter;
  texts: Record<string, string>;
  googleAnalytics: SettingsGoogleAnalytics;
  organisation: SettingsOrganisation | null;
  website: SettingsWebsite | null;
};

export type SettingsOrganisation = {
  name?: string;
  street?: string | null;
  extendedAddress?: string | null;
  postalCode?: string | null;
  city?: string | null;
  country?: string | null;
  email?: string | null;
};

export type SettingsWebsite = {
  name: string;
  url: string;
};

export type SettingsFooter = {
  address: string;
  socialLinks: {
    id: number;
    url: string;
    image: ImageData;
  }[];
};

export type SettingsGoogleAnalytics = {
  consentInfo?: string;
  acceptAllLabel: string;
  declineAllLabel: string;
};

export type SeoSocialData = {
  socialNetwork: 'Facebook' | 'Twitter';
  title: string;
  description: string;
  image: ImageData | null;
};

export type SeoData = {
  metaTitle: string;
  metaDescription: string;
  metaImageUrl: string;
  metaSocial: SeoSocialData[];
  keywords?: string;
  metaRobots?: string;
  structuredData?: string;
  metaViewport?: string;
  canonicalURL?: string;
};

export type Employee = {
  id: number;
  firstName: string;
  lastName: string;
  middleName?: string;
  titlesSalutation?: string;
  suffix?: string;
  description: string;
  portrait: ImageData | null;
  bio: string;
  linkedIn: string;
};

export type Team = {
  title: string;
  lead: string;
  employees?: Employee[];
};

export type Event = {
  id: number;
  title: string;
  start: string;
  end: string;
  location: string;
  link: Link | null;
  description?: string;
};

export type Events = {
  title: string;
  paragraph: string;
  events: Event[];
};

export type Contact = {
  id: number;
  title: string;
  paragraph: string;
  successMsg: string;
  errorMsg: string;
  showBg: boolean;
  function: string[];
  surgicalSpecialty: string[];
};

export type News = {
  id: string;
  slug: string;
  title: string;
  intro: string;
  publishDate: string;
  updatedAt: string;
  image: ImageData;
  content: Section[];
  seo: SeoData | null;
  locale: string | null;
};

export type BlogAuthor = {
  name: string;
  role: string;
  portrait: ImageData | null;
};

export type Blog = {
  id: string;
  slug: string;
  title: string;
  intro: string;
  publishDate: string;
  updatedAt: string;
  image: ImageData;
  content: Section[];
  seo: SeoData | null;
  locale: string | null;
  author: BlogAuthor | null;
};

export type Button = {
  links: Link[];
};

export type Vimeo = {
  vimeoUrl: string;
  videoPreset?: keyof typeof vimeoPresets | null;
  videoStartAtSecond?: number | null;
  title: string | null;
  description: string | null;
  thumbnail: ImageData | null;
};

export type Highlight = {
  text: string;
};

export type MetaPagination = {
  page: number;
  pageSize: number;
  pageCount: number;
  total: number;
};

export type HTML = {
  html: string;
};

export type VideoCarouselItem = {
  title: string;
  thumbnailTitle: string;
  description: string;
  vimeoUrl: string;
  thumbnail: ImageData | null;
};

export type VideoCarousel = {
  videos: VideoCarouselItem[];
};

export type WhistleblowerForm = {
  title: string;
  paragraph: string;
  notice: string;
  to: string;
};
